// The heatmap's transfer function: which cell goes in which bucket, and what
// a bucket paints. It is the whole ramp and no drawing at all.
//
// The four functions came out of the heatmap layer (R431). That layer drew
// with a renderer, and three consumers need the ramp without one: the legend
// gradient in `ui/format.ts`, the shader ramp of the WebGL2 client, and the
// test that pins the steps.
//
// The rules the functions carry are unchanged, and so is every figure: the
// bucket count and the dark ease ride presentation.toml [presentation.heat]
// (R312), and the alpha span comes from the mode.

import { HEAT_ALPHA_HI, HEAT_ALPHA_LO, type HeatMode } from '../state';
import { BLOCK_RIDGE } from '../world';
import { mixColor } from './color';

/**
 * How many alpha steps the ramp has (presentation.toml: enough that the
 * field reads as continuous, few enough that a rebuild is cheap), and how
 * fast a dark ramp climbs to its mode hue (R258).
 *
 * The alpha ramp is linear and it composites over near-black water, so a
 * colour ramp that is linear as well puts almost all of its lightness in
 * the top half: the faint end pays for the middle. An exponent below 1
 * lifts the colour early.
 *
 * 0.6 is still the exponent under R259's wider SOV span, and it is now the
 * exponent that makes the steps most nearly equal, which is what a numbered
 * legend wants: every step of the SOV ramp measures 0.040 to 0.046 of
 * composited OKLab lightness. A linear mix over the same span runs 0.032 to
 * 0.054, and 0.032 is the flat ramp's smallest step. The exponent rides
 * presentation.toml [presentation.heat] dark_ease.
 */
export { HEAT_BUCKETS, HEAT_DARK_EASE } from '../gen/presentation';
import { HEAT_BUCKETS, HEAT_DARK_EASE } from '../gen/presentation';

/**
 * Bucket a heat field into per-bucket cell lists — the part of the rebuild
 * that has no Graphics in it, split out so a test can pin it.
 *
 * Two cells are dropped rather than drawn: intensity 0, which is the server
 * saying "nothing here", and a ridge, because the tint covers water and only
 * water (R103) and rock is never owned, fought over or worked.
 *
 * Intensity 1..255 spreads evenly over the buckets, so 1 lands in the first
 * bucket and 255 in the last. `blocked` may be null before the hello, and a
 * field shorter than the map is read only as far as it goes.
 */
export function bucketCells(
  cells: Uint8Array, blocked: Uint8Array | null, buckets = HEAT_BUCKETS,
): number[][] {
  const out: number[][] = [];
  for (let b = 0; b < buckets; b++) out.push([]);
  for (let i = 0; i < cells.length; i++) {
    const v = cells[i];
    if (v === 0) continue;
    if (blocked && blocked[i] === BLOCK_RIDGE) continue;
    let b = (((v - 1) * buckets) / 255) | 0;
    if (b >= buckets) b = buckets - 1;
    out[b].push(i);
  }
  return out;
}

/**
 * The alpha a bucket draws at, low bucket to high.
 *
 * `lo` and `hi` are the mode's own span. A mode states neither and takes the
 * shared constants, which is what every mode did before R259.
 */
export function bucketAlpha(
  bucket: number, lo = HEAT_ALPHA_LO, hi = HEAT_ALPHA_HI, buckets = HEAT_BUCKETS,
): number {
  if (buckets <= 1) return hi;
  return lo + (hi - lo) * (bucket / (buckets - 1));
}

/**
 * The colour a bucket draws in, low bucket to high.
 *
 * A mode with no `dark` endpoint paints its one hue at every bucket, which
 * is what every mode did before R258. A mode with one paints that dark
 * shade at the faintest bucket and the full hue at the strongest, and it
 * mixes the two per channel in between.
 */
export function bucketColor(
  color: number, dark: number | undefined, bucket: number, buckets = HEAT_BUCKETS,
): number {
  if (dark === undefined) return color;
  const t = buckets <= 1 ? 1 : (bucket / (buckets - 1)) ** HEAT_DARK_EASE;
  return mixColor(dark, color, t);
}

/**
 * What one bucket of a mode paints: its colour and its alpha (R259).
 *
 * This is the whole transfer function of a heatmap, and it is the one place
 * that holds it. The canvas layer reads it for every fill, and `rampCss` in
 * `ui/format.ts` reads it for every stop of the legend gradient, so the bar
 * under the legend is the field and not a second drawing of it.
 */
export function bucketPaint(
  mode: HeatMode, bucket: number, buckets = HEAT_BUCKETS,
): { color: number; alpha: number } {
  return {
    color: bucketColor(mode.color, mode.dark, bucket, buckets),
    alpha: bucketAlpha(bucket, mode.alphaLo, mode.alphaHi, buckets),
  };
}
